// src/hooks/useStaffingState.js
import { useState, useEffect, useCallback } from 'react';
import { Logger } from '../utils/logger';

const logger = new Logger('useStaffingState');

const STORAGE_KEY = 'staffing_state_v1';

/**
 * useStaffingState - Centralized state management for the Staffing system
 * Provides persistent state that survives tab navigation and browser refresh
 */
const useStaffingState = () => {
  // Initialize state from localStorage or defaults
  const [staffingState, setStaffingState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        logger.debug('Restored staffing state from localStorage:', parsed);
        return {
          // Current workflow state
          currentView: 'hub', // 'hub' | 'workspace' | 'optimization'
          currentStaffingId: null,
          currentOpportunityData: null,
          
          // Team Assembly state
          activeTab: 0, // 0=Assembly, 1=Optimization, 2=Configurations
          selectedRole: null,
          candidatePool: [],
          isSearchingCandidates: false,
          claudeCommentary: '',
          roleCandidatePools: {},
          
          // Team optimization state
          optimizationResult: null,
          teamConfigurations: [
            { id: 'config-a', name: 'Configuration A', isActive: true, assignments: {} }
          ],
          activeConfigId: 'config-a',
          
          // Performance data cache
          candidatePerformanceData: {},

          // UI state
          selectedRoleForDetails: null,
          selectedCandidateForDetails: null,

          // Staffing strategy
          globalStrategy: 'balanced',
          roleStrategies: {},

          // Override with saved state
          ...parsed
        };
      }
    } catch (error) {
      logger.error('Error loading staffing state from localStorage:', error);
    }
    
    // Default state if no saved state or error
    return {
      currentView: 'hub',
      currentStaffingId: null,
      currentOpportunityData: null,
      activeTab: 0,
      selectedRole: null,
      candidatePool: [],
      isSearchingCandidates: false,
      claudeCommentary: '',
      roleCandidatePools: {},
      optimizationResult: null,
      teamConfigurations: [
        { id: 'config-a', name: 'Configuration A', isActive: true, assignments: {} }
      ],
      activeConfigId: 'config-a',
      candidatePerformanceData: {},
      selectedRoleForDetails: null,
      selectedCandidateForDetails: null,
      globalStrategy: 'balanced',
      roleStrategies: {},
    };
  });

  // Save state to localStorage whenever it changes
  useEffect(() => {
    try {
      // Create a serializable version of the state
      // Strip transient isSearching flag from each role pool before saving
      const savedRolePools = {};
      Object.entries(staffingState.roleCandidatePools || {}).forEach(([roleId, pool]) => {
        savedRolePools[roleId] = { ...pool, isSearching: false };
      });
      const stateToSave = {
        ...staffingState,
        // Don't save UI-only state that shouldn't persist
        selectedRoleForDetails: null,
        selectedCandidateForDetails: null,
        roleCandidatePools: savedRolePools,
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      logger.debug('Saved staffing state to localStorage');
    } catch (error) {
      logger.error('Error saving staffing state to localStorage:', error);
    }
  }, [staffingState]);

  // Action creators for updating state
  const actions = {
    // Navigation actions
    setCurrentView: useCallback((view) => {
      setStaffingState(prev => ({ ...prev, currentView: view }));
    }, []),

    setCurrentStaffing: useCallback((staffingId, opportunityData = null) => {
      const hasLineupData = opportunityData && (
        (opportunityData.progress?.filledRoles > 0) ||
        (opportunityData.configurations?.count > 0)
      );
      setStaffingState(prev => ({
        ...prev,
        currentStaffingId: staffingId,
        currentOpportunityData: opportunityData,
        currentView: 'workspace',
        activeTab: hasLineupData ? 2 : 0
      }));
    }, []),

    goBackToHub: useCallback(() => {
      setStaffingState(prev => ({
        ...prev,
        currentView: 'hub',
        currentStaffingId: null,
        // Keep opportunity data for potential return
        // currentOpportunityData: null,
      }));
    }, []),

    // Team Assembly actions
    setActiveTab: useCallback((tabIndex) => {
      setStaffingState(prev => ({ ...prev, activeTab: tabIndex }));
    }, []),

    setSelectedRole: useCallback((role) => {
      setStaffingState(prev => ({ ...prev, selectedRole: role }));
    }, []),

    setCandidatePool: useCallback((candidates) => {
      setStaffingState(prev => ({ ...prev, candidatePool: candidates }));
    }, []),

    setIsSearchingCandidates: useCallback((isSearching) => {
      setStaffingState(prev => ({ ...prev, isSearchingCandidates: isSearching }));
    }, []),

    setClaudeCommentary: useCallback((commentary) => {
      setStaffingState(prev => ({ ...prev, claudeCommentary: commentary }));
    }, []),

    // Per-role candidate pool actions
    setRoleCandidates: useCallback((roleId, candidates, commentary) => {
      setStaffingState(prev => ({
        ...prev,
        roleCandidatePools: {
          ...prev.roleCandidatePools,
          [roleId]: { ...(prev.roleCandidatePools[roleId] || {}), candidates, commentary, isSearching: false },
        },
      }));
    }, []),

    setRoleSearching: useCallback((roleId, isSearching) => {
      setStaffingState(prev => ({
        ...prev,
        roleCandidatePools: {
          ...prev.roleCandidatePools,
          [roleId]: { ...(prev.roleCandidatePools[roleId] || {}), isSearching },
        },
      }));
    }, []),

    setRoleSearchCandidate: useCallback((roleId, candidate) => {
      setStaffingState(prev => ({
        ...prev,
        roleCandidatePools: {
          ...prev.roleCandidatePools,
          [roleId]: { ...(prev.roleCandidatePools[roleId] || {}), searchCandidate: candidate },
        },
      }));
    }, []),

    clearRoleCandidates: useCallback((roleId) => {
      setStaffingState(prev => {
        const next = { ...prev.roleCandidatePools };
        delete next[roleId];
        return { ...prev, roleCandidatePools: next };
      });
    }, []),

    // Staffing strategy actions
    setGlobalStrategy: useCallback((strategyId) => {
      setStaffingState(prev => ({ ...prev, globalStrategy: strategyId, roleStrategies: {} }));
    }, []),

    setRoleStrategy: useCallback((roleId, strategyId) => {
      setStaffingState(prev => ({
        ...prev,
        roleStrategies: {
          ...prev.roleStrategies,
          [roleId]: strategyId || undefined,
        },
      }));
    }, []),

    // Performance data cache actions
    setCandidatePerformanceData: useCallback((employeeId, data) => {
      setStaffingState(prev => ({
        ...prev,
        candidatePerformanceData: {
          ...prev.candidatePerformanceData,
          [employeeId]: data
        }
      }));
    }, []),

    // Team optimization actions
    setOptimizationResult: useCallback((result) => {
      setStaffingState(prev => ({ ...prev, optimizationResult: result }));
    }, []),

    setTeamConfigurations: useCallback((configurations) => {
      setStaffingState(prev => ({ ...prev, teamConfigurations: configurations }));
    }, []),

    setActiveConfigId: useCallback((configId) => {
      setStaffingState(prev => ({ ...prev, activeConfigId: configId }));
    }, []),

    // UI state actions
    setSelectedRoleForDetails: useCallback((role) => {
      setStaffingState(prev => ({ ...prev, selectedRoleForDetails: role }));
    }, []),

    setSelectedCandidateForDetails: useCallback((candidate) => {
      setStaffingState(prev => ({ ...prev, selectedCandidateForDetails: candidate }));
    }, []),

    // Bulk state update for complex operations
    updateStaffingState: useCallback((updates) => {
      setStaffingState(prev => ({ ...prev, ...updates }));
    }, []),

    // Clear all state (for logout, etc.)
    clearStaffingState: useCallback(() => {
      localStorage.removeItem(STORAGE_KEY);
      setStaffingState({
        currentView: 'hub',
        currentStaffingId: null,
        currentOpportunityData: null,
        activeTab: 0,
        selectedRole: null,
        candidatePool: [],
        isSearchingCandidates: false,
        claudeCommentary: '',
        roleCandidatePools: {},
        optimizationResult: null,
        teamConfigurations: [
          { id: 'config-a', name: 'Configuration A', isActive: true, assignments: {} }
        ],
        activeConfigId: 'config-a',
        candidatePerformanceData: {},
        selectedRoleForDetails: null,
        selectedCandidateForDetails: null
      });
    }, [])
  };

  // Helper functions
  const helpers = {
    // Check if we're currently in a specific workflow
    isInWorkspace: () => staffingState.currentView === 'workspace',
    isInHub: () => staffingState.currentView === 'hub',
    isInOptimization: () => staffingState.currentView === 'optimization',

    // Check if we have an active staffing session
    hasActiveStaffing: () => !!staffingState.currentStaffingId,

    // Get current opportunity data
    getCurrentOpportunity: () => staffingState.currentOpportunityData,

    // Get cached performance data for an employee
    getCandidatePerformanceData: (employeeId) => 
      staffingState.candidatePerformanceData[employeeId] || null,

    // Get current active configuration
    getActiveConfiguration: () => 
      staffingState.teamConfigurations.find(config => 
        config.id === staffingState.activeConfigId
      ) || staffingState.teamConfigurations[0]
  };

  return {
    // State
    staffingState,
    
    // Actions
    ...actions,
    
    // Helpers
    ...helpers
  };
};

export default useStaffingState;
